import { useEffect, useMemo, useState } from 'react'
import { Row, Col } from 'react-bootstrap'
import { Button, FormControl as Input } from 'react-bootstrap'
import { Form, Formik } from 'formik'
import { DropdownList } from 'layouts/DropdownList'
import {
  IDropdownData,
  AddressTypes,
  LocationDropDown,
  SaveLocationOption,
  initialAddressValue,
  IAddressForm,
  IOption
} from 'types/baseTypes'
import { ValidationErrorMessage } from 'components/Forms'
import * as yup from 'yup'
import { GENERAL_ERROR_MESSAGE } from 'common/constants'
import { DeliveryInformation } from 'types/vehicleTypes'
import { CustomRadioButton } from './VDP/CustomRadioButton'
import { StyledAddressModal } from './AddressModel.styled'
import { getStatesByCountryID } from 'apis/vehicleApis'

interface IAddressProps {
  defaultAddressDetail: LocationDropDown
  show: boolean
  deliveryInformation: DeliveryInformation
  events: {
    onClose: any
    handleSaveAddress: any
    setIsSaveAddressToBuyer: any
  }
}

export const AddressModal = ({ defaultAddressDetail, show, deliveryInformation, events }: IAddressProps) => {
  const [isSubmit, setIsSubmit] = useState(false)
  const [addressDetail] = useState<LocationDropDown>(defaultAddressDetail)
  const [initLocationData, setInitLocationData] = useState<IAddressForm>(initialAddressValue)
  const [isLoading, setIsLoading] = useState(true)
  const [saveOptionSelected, setSaveOptionSelected] = useState(0)
  const [dynamicStates, setDynamicStates] = useState<IDropdownData[]>([])
  const [isLoadingStates, setIsLoadingStates] = useState(false)
  const [hasCountryBeenSelected, setHasCountryBeenSelected] = useState(false)

  const addressValidateSchema = yup.object({
    address: yup.string().trim().required(GENERAL_ERROR_MESSAGE),
    city: yup.string().trim().required(GENERAL_ERROR_MESSAGE),
    zip: yup.string().trim().required(GENERAL_ERROR_MESSAGE),
    stateCode: yup.string().notOneOf(['-1'], GENERAL_ERROR_MESSAGE),
    countryCode: yup.string().notOneOf(['-1'], GENERAL_ERROR_MESSAGE)
  })

  useEffect(() => {
    if (defaultAddressDetail != null) {
      setInitLocationData({
        address: defaultAddressDetail.Address1,
        address2: defaultAddressDetail.Address2,
        city: defaultAddressDetail.City,
        zip: defaultAddressDetail.Zip,
        stateCode: defaultAddressDetail?.StateCode,
        countryCode: defaultAddressDetail?.CountryCode
      })
    } else {
      // Set default country when no default address detail
      setInitLocationData({
        ...initialAddressValue,
        countryCode: deliveryInformation?.DefaultCountry?.toString() || '-1'
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAddressDetail, deliveryInformation?.DefaultCountry])

  useEffect(() => {
    if (deliveryInformation?.Countries) {
      setIsLoading(false)
    }
  }, [deliveryInformation?.Countries])

  const handleSaveAddress = (values: IAddressForm) => {
    events.handleSaveAddress({
      AddressLineOne: values.address,
      AddressLineTwo: values.address2,
      Town: values.city,
      StateId: Number(values.stateCode),
      PostCode: values.zip,
      AddressId: defaultAddressDetail?.AddressTypeID !== AddressTypes.HEAD_OFFICE ? defaultAddressDetail?.ID : 0,
      CountryId: Number(values.countryCode),
      AddressTypeId: AddressTypes.DELIVERY
    })
  }

  const countryList: IOption<IDropdownData>[] = useMemo(() => {
    return deliveryInformation?.Countries?.map((country) => ({
      item: country,
      label: country.Description,
      value: country.ID
    }))
  }, [deliveryInformation?.Countries])

  const stateList: IOption<IDropdownData>[] = useMemo(() => {
    // Use dynamic states if a country has been selected, otherwise use deliveryInformation.States
    const statesToUse = hasCountryBeenSelected ? dynamicStates : deliveryInformation?.States || []
    return statesToUse.map((state) => ({
      item: state,
      label: state.Description,
      value: state.ID
    }))
  }, [dynamicStates, deliveryInformation?.States, hasCountryBeenSelected])

  const handleCountryChange = async (countryId: string, formikProps: any) => {
    formikProps.setFieldValue('countryCode', countryId)
    formikProps.setFieldValue('stateCode', '-1')

    if (countryId === '-1') {
      setDynamicStates([])
      setHasCountryBeenSelected(false)
      return
    }

    setHasCountryBeenSelected(true)
    try {
      setIsLoadingStates(true)
      const states = await getStatesByCountryID(parseInt(countryId))
      setDynamicStates(states)
    } catch (error) {
      console.error('Error fetching states:', error)
      setDynamicStates([])
    } finally {
      setIsLoadingStates(false)
    }
  }

  if (!show) {
    return null // Do not render anything if `show` is false
  }
  const handleSaveOptionChange = (e: any) => {
    setSaveOptionSelected(Number(e.target.id))
    if (Number(e.target.id) === SaveLocationOption.SAVE_TO_BUYER) {
      events.setIsSaveAddressToBuyer(true)
    } else {
      events.setIsSaveAddressToBuyer(false)
    }
  }

  return (
    <StyledAddressModal>
      <div style={{ marginTop: '20px' }}>
        <Formik
          enableReinitialize
          initialValues={initLocationData}
          validationSchema={addressValidateSchema}
          onSubmit={(values) => {
            if (saveOptionSelected === 0) {
              setIsSubmit(true)
              return
            }
            setIsSubmit(false)
            handleSaveAddress(values)
          }}
          validateOnBlur={true}
          validateOnChange={false}
        >
          {(props) => (
            <Form>
              <div>
                <label className="required-field">Address</label>
                <Input
                  name="address"
                  defaultValue={addressDetail?.Address1}
                  isInvalid={(isSubmit || props.touched?.address) && !!props.errors?.address}
                  onChange={props.handleChange}
                  onBlur={props.handleBlur}
                  value={props.values?.address}
                />
                <ValidationErrorMessage>
                  {(isSubmit || props.touched?.address) && !!props.errors?.address && props.errors?.address}
                </ValidationErrorMessage>
              </div>
              <div style={{ marginTop: '8px' }}>
                <label>Address 2</label>
                <Input
                  name="address2"
                  defaultValue={addressDetail?.Address2}
                  onChange={props.handleChange}
                  onBlur={props.handleBlur}
                  value={props.values?.address2}
                />
                <p className="error-label"></p>
              </div>
              <Row style={{ marginTop: '8px' }}>
                <Col sm={6}>
                  <div>
                    <label className="required-field">City</label>
                    <Input
                      name="city"
                      defaultValue={addressDetail?.City}
                      isInvalid={(isSubmit || props.touched?.city) && !!props.errors?.city}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      value={props.values?.city}
                    />
                    <ValidationErrorMessage>
                      {(isSubmit || props.touched?.city) && !!props.errors?.city && props.errors?.city}
                    </ValidationErrorMessage>
                  </div>
                </Col>
                <Col sm={6}>
                  <div>
                    <label className="required-field">Country</label>
                    <DropdownList
                      defaultValue={deliveryInformation?.DefaultCountry.toString() || '-1'}
                      items={
                        isLoading
                          ? [{ id: '-1', value: 'Loading...' }]
                          : [
                              { id: '-1', value: 'Choose a Country' },
                              ...countryList?.map((country) => ({
                                id: country.value.toString(),
                                value: country.label
                              }))
                            ]
                      }
                      onChange={(selectedId) => {
                        handleCountryChange(selectedId, props)
                        props.setFieldTouched('countryCode', true)
                      }}
                    />
                    <ValidationErrorMessage>
                      {(isSubmit || props.touched?.countryCode) &&
                        !!props.errors?.countryCode &&
                        props.errors?.countryCode}
                    </ValidationErrorMessage>
                  </div>
                </Col>
              </Row>
              <Row style={{ marginTop: '8px' }}>
                <Col sm={6}>
                  <label className="required-field">State</label>
                  <DropdownList
                    defaultValue={stateList?.[0]?.value || '-1'}
                    items={
                      isLoadingStates
                        ? [{ id: '-1', value: 'Loading states...' }]
                        : stateList.length === 0
                        ? [{ id: '-1', value: 'No states available' }]
                        : [
                            { id: '-1', value: 'Choose a State' },
                            ...stateList?.map((state) => ({
                              id: state.value.toString(),
                              value: state.label
                            }))
                          ]
                    }
                    onChange={(selectedId) => {
                      props.setFieldValue('stateCode', selectedId)
                      props.setFieldValue('stateCode', selectedId, false)
                      setTimeout(() => {
                        props.setFieldTouched('stateCode', true)
                      }, 10)
                    }}
                  />
                  <ValidationErrorMessage>
                    {(isSubmit || props.touched?.stateCode) && !!props.errors?.stateCode && props.errors?.stateCode}
                  </ValidationErrorMessage>
                </Col>
                <Col sm={6}>
                  <div>
                    <label className="required-field">Zip Code</label>
                    <Input
                      name="zip"
                      maxLength={5}
                      defaultValue={addressDetail?.Zip}
                      isInvalid={(isSubmit || props.touched?.zip) && !!props.errors?.zip}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      value={props.values?.zip}
                    />
                    <ValidationErrorMessage>
                      {(isSubmit || props.touched?.zip) && !!props.errors?.zip && props.errors.zip}
                    </ValidationErrorMessage>
                  </div>
                </Col>
              </Row>
              <Row className="save-option">
                <Col sm={12}>
                  <CustomRadioButton
                    id="1"
                    name="saveOption"
                    value="other"
                    checked={saveOptionSelected === 1}
                    onChange={(e) => handleSaveOptionChange(e)}
                  />
                  <label htmlFor="1">Add to My Locations</label>
                </Col>
                <Col sm={12}>
                  <CustomRadioButton
                    id="2"
                    name="saveOption"
                    value="other"
                    checked={saveOptionSelected === 2}
                    onChange={(e) => handleSaveOptionChange(e)}
                  />
                  <label htmlFor="2">Do not add to My Locations</label>
                </Col>

                <Col sm={12}>
                  <ValidationErrorMessage>
                    {isSubmit && saveOptionSelected === 0 && 'Please select an option'}
                  </ValidationErrorMessage>
                </Col>
              </Row>
              <div className="button-group">
                <Button
                  type="button"
                  variant="primary"
                  style={{ marginRight: '10px' }}
                  onClick={() => {
                    setIsSubmit(true)
                    props.handleSubmit()
                  }}
                >
                  Save
                </Button>
                <Button
                  type="cancel"
                  variant="light"
                  onClick={() => {
                    events.onClose()
                    setIsSubmit(false)
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </StyledAddressModal>
  )
}
